import { useEffect, useRef } from "react";
import { X } from "lucide-react";

/**
 * VideoModal — calm, on-brand player for portrait videos.
 *
 * Designed for Pip's BigVu phone-shot teaching videos. Locks scroll while
 * open, closes on Escape + backdrop click, autoplays on mount, includes
 * native controls. Portrait aspect ratio is preserved (no awkward stretching
 * or auto-cropping).
 */
export default function VideoModal({ src, poster, title, onClose }) {
  const videoRef = useRef(null);
  const closeBtnRef = useRef(null);

  useEffect(() => {
    // Lock body scroll
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Focus close button for keyboard users
    closeBtnRef.current?.focus();

    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const onBackdrop = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-ink/85 backdrop-blur-sm flex items-center justify-center p-4 md:p-8 animate-in fade-in duration-200"
      onClick={onBackdrop}
      role="dialog"
      aria-modal="true"
      aria-label={title || "Video"}
      data-testid="video-modal"
    >
      <button
        ref={closeBtnRef}
        type="button"
        onClick={onClose}
        className="absolute top-5 right-5 md:top-7 md:right-7 w-11 h-11 inline-flex items-center justify-center rounded-full bg-cream/10 hover:bg-cream/20 text-cream transition-colors"
        aria-label="Close video"
        data-testid="video-modal-close"
      >
        <X size={20} strokeWidth={1.8} />
      </button>

      <div className="relative max-h-[92vh] flex flex-col items-center">
        <video
          ref={videoRef}
          src={src}
          poster={poster}
          controls
          autoPlay
          playsInline
          preload="metadata"
          className="max-h-[88vh] max-w-full rounded-2xl shadow-2xl bg-black"
          data-testid="video-modal-player"
        >
          Your browser does not support the video tag.
        </video>
        {title && (
          <div className="mt-5 text-cream/80 text-sm tracking-wide text-center">
            {title}
          </div>
        )}
      </div>
    </div>
  );
}
